import { useCallback, useEffect } from 'react'
import { sendEvent } from '@/services/mixPanelService'
import { useGenericErrorStore } from '@/store/useGenericErrorStore'
import { useSocketStore } from '@/store/useSocketStore'
import * as Sentry from '@sentry/react'

export const useGenericErrorMixpanel = () => {
  const resetAxiosError = useGenericErrorStore((state) => state.resetError)
  const resetErrorSocket = useGenericErrorStore(
    (state) => state.resetErrorSocket
  )

  const sendMixpanelEvent = useCallback((type: string, errorProp?: any) => {
    // captureError(errorSocket || error)
    const error = errorProp || useGenericErrorStore.getState().error
    const errorSocket = useGenericErrorStore.getState().errorSocket
    const maxRetryCount = useSocketStore.getState().maxRetryCount
    const timeToFailConnection = useSocketStore.getState().timeToFailConnection

    // Caso 1: SOCKET (Server Sent Events) com erro
    if (errorSocket?.error || errorSocket?.name) {
      const sentryId = Sentry.captureException(errorSocket, {
        level: 'error',
        tags: {
          errorBoundary: 'useGenericErrorMixpanel-ErrorSocket',
          errorName: errorSocket?.name,
          errorSource: 'generic-error-handler'
        },
        extra: {
          errorMessage: errorSocket?.message,
          errorName: errorSocket?.name,
          errorStack: errorSocket?.stack,
          errorStatusCode: errorSocket?.statusCode,
          errorCode: errorSocket?.code || errorSocket?.error,
          originPage: window.location.href
        }
      })

      sendEvent({
        eventName: 'SHOW_GENERIC_ERROR_BACKEND',
        data: {
          TYPE: `${type} - socket_backend_error`,
          SENTRY_EVENT_ID: sentryId,
          MAX_RETRY_COUNT_SOCKET: maxRetryCount,
          TIME_TO_FAIL_CONNECTION_SOCKET: timeToFailConnection,
          GENERIC_ERROR_NAME: errorSocket?.name || 'Unknown Socket Error',
          GENERIC_ERROR_CODE: errorSocket?.statusCode || 'N/A',
          GENERIC_ERROR_ORIGIN_PAGE:
            errorSocket?.originURL || document.referrer,
          GENERIC_ERROR_MESSAGE:
            errorSocket?.message || 'Unknown Socket message',
          GENERIC_ERROR_STATUS_TEXT: errorSocket?.error || 'N/A',
          GENERIC_ERROR_STACK: errorSocket.stack || 'N/A',
          GENERIC_ERROR_TYPE: errorSocket?.errorType || 'N/A',
          GENERIC_ERROR_DATA: errorSocket?.code || 'N/A',
          REQUISITION_PARAMS: errorSocket?.payload || 'N/A'
        }
      })
      return
    }

    const originPage =
      error?.originURL || document.referrer || window.location.href

    const sentryId = Sentry.captureException(error, {
      level: 'error',
      tags: {
        errorBoundary: 'useGenericErrorMixpanel',
        errorName: error?.name,
        errorSource: 'generic-error-handler'
      },
      extra: {
        errorMessage: error?.message,
        errorName: error?.name,
        errorStack: error?.stack,
        errorDigest: (error as any)?.digest,
        errorCause: (error as any)?.cause,
        originPage:
          typeof window !== 'undefined' ? window.location.href : 'server-side',
        responseStatus: error?.response?.status,
        responseData: error?.response?.data,
        requestUrl: error?.config?.url,
        requestMethod: error?.config?.method
      }
    })

    // Caso 2: Erro com resposta do backend
    if (error?.response) {
      sendEvent({
        eventName: 'SHOW_GENERIC_ERROR_BACKEND',
        data: {
          TYPE: `${type} - backend_error`,
          SENTRY_EVENT_ID: sentryId,
          REQUISITION_URL: error.config?.url || 'N/A',
          REQUISITION_METHOD: error.config?.method || 'N/A',
          REQUISITION_PARAMS: error.config?.data || 'N/A',
          GENERIC_ERROR_ORIGIN_PAGE: originPage,
          BACKEND_ERROR_CODE:
            error.response?.data?.code || error?.code || 'N/A',
          BACKEND_MESSAGE_ERROR:
            error.response?.data?.message ||
            error.message ||
            'Erro com resposta',
          GENERIC_ERROR_DATA: JSON.stringify(
            error.response?.data?.errors || ''
          ),
          GENERIC_ERROR_STATUS: error.response?.status || 'N/A',
          GENERIC_ERROR_STATUS_TEXT:
            error.response?.statusText || error.response?.data?.name || 'N/A',
          GENERIC_ERROR_STACK:
            error?.stack || error.response?.data?.stack || 'N/A'
        }
      })
      return
    }

    // Caso 3: Erro com request mas sem resposta
    if (error?.request) {
      sendEvent({
        eventName: 'SHOW_GENERIC_ERROR_BACKEND',
        data: {
          TYPE: `${type} - no_response_error`,
          SENTRY_EVENT_ID: sentryId,
          REQUISITION_URL: error.config?.url || 'N/A',
          REQUISITION_METHOD: error.config?.method || 'N/A',
          GENERIC_ERROR_ORIGIN_PAGE: originPage,
          GENERIC_ERROR_MESSAGE:
            error.message || 'Erro sem resposta do backend',
          GENERIC_ERROR_CODE: error.code,
          REQUISITION_PARAMS: error.config?.data
        }
      })
      return
    }

    // Caso 4: Erro local
    sendEvent({
      eventName: 'SHOW_GENERIC_ERROR',
      data: {
        TYPE: `${type} - local_error`,
        REQUISITION_URL: error?.config?.url || 'N/A',
        SENTRY_EVENT_ID: sentryId,
        GENERIC_ERROR_ORIGIN_PAGE: originPage,
        GENERIC_ERROR_CODE: errorSocket?.statusCode || 'N/A',
        GENERIC_ERROR_STATUS_TEXT:
          errorSocket?.error || errorSocket?.code || 'N/A',
        GENERIC_ERROR_NAME: error?.name || errorSocket?.name || 'UnknownError',
        GENERIC_ERROR_MESSAGE:
          error?.message ||
          errorSocket?.message ||
          'Erro inesperado no frontend',
        GENERIC_ERROR_STACK: error?.stack || errorSocket?.stack || 'N/A',
        REQUISITION_PARAMS: error?.config?.data || 'N/A'
      }
    })
  }, [])

  useEffect(() => {
    return () => {
      resetAxiosError()
      resetErrorSocket()
    }
  }, [resetAxiosError, resetErrorSocket])

  return { sendMixpanelEvent }
}
